import { type MouseEvent } from 'react';

import { InlineTextEditor } from '@/components/diagrams/components/InlineTextEditor/InlineTextEditor';
import {
  TILE_DEFAULT_FONT_SIZE_VARIANT,
  TILE_DEFAULT_VARIANT,
} from '@/components/diagrams/components/Tile/constants';
import { TileThemedTypography } from '@/components/diagrams/components/Tile/TileThemedTypography';
import { DiagramNodeDisplay } from '@/components/diagrams/diagramStyle';
import { COLORS } from '@/styles/tokens/colors';

import { type DiagramBuilderNode } from '../../utils/diagram';
import { type ReactFlowDiagramNodeData } from '../../utils/reactFlowDiagram';
import {
  DiagramNotesEditor,
  type DiagramNotesEditorProps,
  DiagramNotesMarkdown,
} from './DiagramNotes';

/** The text fields of a node, in the order that Tab moves through them. */
export const TEXT_FIELDS = [
  'label',
  'subtitle',
  'detail',
  'footerLabel',
] as const satisfies readonly (keyof DiagramBuilderNode)[];
export type DiagramTextFieldName = (typeof TEXT_FIELDS)[number];

/**
 * The data attribute that marks each field, so a double-click edits the
 * field under the pointer.
 */
export const FIELD_ATTRIBUTE = 'data-diagram-field';

/** The accessible name and the placeholder of the editor of each field. */
const TEXT_FIELD_NAME: Record<DiagramTextFieldName, string> = {
  label: 'Title',
  subtitle: 'Subtitle',
  detail: 'Notes',
  footerLabel: 'Footer',
};

/**
 * The opacity of the placeholder of an empty field. The placeholder uses the
 * text color of the node, so it shows on light and dark tiles.
 */
const PLACEHOLDER_OPACITY = 0.5;

interface FieldTypography {
  variant: 'title-s' | 'title-xs' | 'body-xs';
  /**
   * The space above a field that follows another field in the same
   * container, in theme spacing units, so the nodes need no spacing rules
   * of their own.
   */
  spaceAbove: number;
}

/**
 * The typography of each field on tiles and standalone text, as in the Figma
 * component anatomy. The color and the font size setting of the node color
 * and scale the text.
 */
const FIELD_TYPOGRAPHY: Record<DiagramTextFieldName, FieldTypography> = {
  label: { variant: 'title-s', spaceAbove: 0 },
  subtitle: { variant: 'body-xs', spaceAbove: 0.5 },
  detail: { variant: 'body-xs', spaceAbove: 1.5 },
  footerLabel: { variant: 'body-xs', spaceAbove: 1.5 },
};

/** A shape label is smaller than a tile title, as in the Figma shapes. */
const SHAPE_LABEL_TYPOGRAPHY: FieldTypography = {
  variant: 'title-xs',
  spaceAbove: 0,
};

/** An icon stacks its fields with a small gap, as in the Figma icon. */
const ICON_DETAIL_TYPOGRAPHY: FieldTypography = {
  variant: 'body-xs',
  spaceAbove: 0.5,
};

function getFieldTypography(
  field: DiagramTextFieldName,
  componentType: ReactFlowDiagramNodeData['componentType']
): FieldTypography {
  if (field === 'label' && componentType === DiagramNodeDisplay.Shape) {
    return SHAPE_LABEL_TYPOGRAPHY;
  }
  if (field === 'detail' && componentType === DiagramNodeDisplay.TileIcon) {
    return ICON_DETAIL_TYPOGRAPHY;
  }
  return FIELD_TYPOGRAPHY[field];
}

/** The editor callbacks of a field. The notes editor also uses `onSave`. */
export type DiagramTextFieldEditor = Omit<
  DiagramNotesEditorProps,
  'label' | 'value'
>;

interface DiagramTextFieldProps {
  field: DiagramTextFieldName;
  /** The data of the node, for its colors and font size. */
  data: ReactFlowDiagramNodeData;
  /** The text of the field. The notes are Markdown. */
  text: string;
  /** The saved text that the editor edits. */
  savedText: string;
  /** The editor callbacks while the user edits this field, or null. */
  editor: DiagramTextFieldEditor | null;
  /**
   * Edits this field. While the user edits the node text, a press on the
   * placeholder of an empty field calls it. Without it, the placeholder is
   * plain text, and a press selects the node as on its other text.
   */
  onEdit?: () => void;
}

/**
 * One text field of a builder node in the typography of the field. It shows
 * the editor of the field while the user edits it, the text of the field, or
 * a placeholder when the field has no text. The notes show formatted, and
 * the user edits them in a rich-text editor. The other fields are plain
 * text. The editor takes the font of the field, so a field looks the same
 * while the user edits it.
 */
export function DiagramTextField({
  field,
  data,
  text,
  savedText,
  editor,
  onEdit,
}: DiagramTextFieldProps) {
  const {
    variant = TILE_DEFAULT_VARIANT,
    customColor,
    fontSizeVariant = TILE_DEFAULT_FONT_SIZE_VARIANT,
    componentType,
    tileEmphasis,
  } = data;
  const { variant: typographyVariant, spaceAbove } = getFieldTypography(
    field,
    componentType
  );
  const isNotes = field === 'detail';
  const isIcon = componentType === DiagramNodeDisplay.TileIcon;

  const renderContent = () => {
    // 1. This is the field that is open for editing: show its editor. Notes
    //    use the rich-text editor; the other fields use a plain-text editor.
    if (editor) {
      const editorProps = { label: TEXT_FIELD_NAME[field], value: savedText };
      if (isNotes) return <DiagramNotesEditor {...editorProps} {...editor} />;
      return (
        <InlineTextEditor
          {...editorProps}
          onCommit={editor.onCommit}
          onCancel={editor.onCancel}
          onTab={editor.onTab}
        />
      );
    }
    // 2. This field is not open for editing and has text: show the text.
    //    Notes show as formatted Markdown.
    if (text && isNotes) return <DiagramNotesMarkdown markdown={text} />;
    if (text) return text;
    // 3. This field is empty, and no field of the node is open for editing.
    //    Only the main field shows in this state, such as the notes of an
    //    empty TEXT node. Its placeholder is plain text: a press selects the
    //    node, and a double-click opens the field for editing. A press cannot
    //    open the editor here, because the editor closes when its node is not
    //    selected.
    if (!onEdit) {
      return (
        <span style={{ opacity: PLACEHOLDER_OPACITY }}>
          {TEXT_FIELD_NAME[field]}
        </span>
      );
    }
    // 4. This field is empty, and another field of the same node is open for
    //    editing. A press on the placeholder saves the open field and opens
    //    this field instead. The press does not start a node drag, which would
    //    move the focus and close the open field first.
    return (
      <span
        className="nodrag"
        style={{ opacity: PLACEHOLDER_OPACITY }}
        onMouseDown={(event) => {
          event.preventDefault();
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
          onEdit();
        }}
      >
        {TEXT_FIELD_NAME[field]}
      </span>
    );
  };

  return (
    <TileThemedTypography
      {...{ [FIELD_ATTRIBUTE]: field }}
      variant={typographyVariant}
      // The notes are Markdown with paragraphs and lists. The other fields
      // can be in a paragraph of the shared tile.
      component={isNotes ? 'div' : 'span'}
      tileVariant={variant}
      tileCustomColor={customColor}
      // An icon draws its text outside its colored body.
      tileEmphasis={isIcon ? undefined : tileEmphasis}
      fontSizeVariant={fontSizeVariant}
      // A double-click in an open editor selects a word. It does not edit
      // another field.
      onDoubleClick={
        editor ? (event: MouseEvent) => event.stopPropagation() : undefined
      }
      sx={{
        display: 'block',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        '&:not(:first-child)': { pt: spaceAbove },
        // The footer of an icon is secondary text below its notes. The
        // doubled selector wins over the tile text color.
        ...(isIcon &&
          field === 'footerLabel' && { '&&': { color: COLORS.GRAY[500] } }),
      }}
    >
      {renderContent()}
    </TileThemedTypography>
  );
}
